import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { Btn, Badge, DataTable } from "@/components/ui-lite";
import { toast } from "sonner";
import { PlayCircle } from "lucide-react";

export default function AdminSqlPage() {
  const [sql, setSql] = useState("SELECT DATABASE(), NOW();");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadHistory = () => api.get("/sql/history").then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []);

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/sql/execute", { sql });
      setResult(data);
      if (data.ok) toast.success(`Rows: ${data.count ?? data.rows_affected ?? 0}`);
      else toast.error(data.error);
      loadHistory();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setLoading(false); }
  };

  return (
    <AppShell title="SQL Editor" subtitle="Ad-hoc queries against the remote MySQL via the jumphost. Read-only and write both allowed.">
      <div className="wv-card p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="wv-overline">Query</div>
          <Btn data-testid="sql-run" onClick={run} disabled={loading}><PlayCircle size={16} className="inline mr-1"/>{loading ? "Running..." : "Run (⌘⏎)"}</Btn>
        </div>
        <textarea
          data-testid="sql-input"
          value={sql}
          onChange={(e) => setSql(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") run(); }}
          rows={12}
          spellCheck={false}
          className="wv-sql-editor"
        />
      </div>

      {result && (
        <div className="wv-card p-6 mt-6" data-testid="sql-result">
          <div className="flex items-center justify-between mb-3">
            <div className="wv-overline">Result</div>
            {result.ok
              ? <Badge tone="success">{result.count ?? result.rows_affected} rows</Badge>
              : <Badge tone="danger">{result.error}</Badge>}
          </div>
          {result.ok && result.columns?.length > 0 && (
            <div className="overflow-x-auto max-h-96 overflow-y-auto rounded-xl">
              <table className="w-full text-left text-xs font-mono">
                <thead className="bg-white/70 sticky top-0">
                  <tr>{result.columns.map((c) => <th key={c} className="py-2 px-3">{c}</th>)}</tr>
                </thead>
                <tbody>
                  {result.rows.slice(0, 500).map((r, i) => (
                    <tr key={i} className="border-t border-white/60">{result.columns.map((c) => <td key={c} className="py-1.5 px-3">{r[c] == null ? <span className="text-[var(--wv-text-3)]">NULL</span> : String(r[c])}</td>)}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="mt-6">
        <div className="wv-overline mb-3">History</div>
        <DataTable
          columns={[
            { key: "at", label: "When", mono: true, render: (r) => r.at?.slice(0, 19).replace("T", " ") },
            { key: "by", label: "By" },
            { key: "sql", label: "SQL", render: (r) => <code className="font-mono text-xs">{r.sql?.slice(0, 120)}{r.sql?.length > 120 ? "…" : ""}</code> },
            { key: "row_count", label: "Rows", mono: true, render: (r) => r.ok ? r.row_count ?? 0 : <Badge tone="danger">err</Badge> },
          ]}
          rows={history}
          actions={(r) => <button onClick={() => setSql(r.sql)} className="text-xs text-[var(--wv-primary)] hover:underline">Load</button>}
        />
      </div>
    </AppShell>
  );
}
